<template>
  <v-container data-test-id="dataTableContainer" fluid class="main-container">
    <v-row
      v-if="!appStore.isPrinting"
      :class="{ 'top-row': !title, 'top-row-small': title }"
      align="center"
    >
      <SearchField
        data-test-id="dataTableSearchField"
        v-if="searchType === SearchType.FieldSearch"
        v-model.trim="store.searchText"
        :max-width="225"
      />
      <h3 data-test-id="dataTableTitle" v-if="title" class="table-title">{{ title }}:</h3>
      <ButtonIcon
        data-test-id="dataTableRefreshButton"
        v-if="showRefresh"
        :class="{
          'with-search': searchType === SearchType.FieldSearch
        }"
        :text="t('buttons.refreshData')"
        icon="mdi-database-refresh"
        @click="store.loadItems()"
      />
      <slot name="left"></slot>
      <v-spacer />
      <ButtonPrimary
        data-test-id="dataTableNewButton"
        v-if="showNew && editable"
        class="button-new"
        density="compact"
        :disabled="disableNew"
        @click="createNewItem"
        :text="t('buttons.new')"
      />
    </v-row>
    <v-row style="height: 1.25rem" v-else></v-row>
    <v-row class="table-row">
      <v-data-table-server
        data-test-id="dataTable"
        return-object
        class="data-table"
        v-bind="$attrs"
        density="compact"
        v-model="model"
        :mobile="false"
        v-model:items-per-page="store.itemsPerPage"
        :items="store.items"
        :items-length="store.count"
        :loading="store.workStatus?.search || store.workStatus?.getMany"
        item-value="id"
        :search="store.searchText"
        :items-per-page-options="settingsStore.itemsPerPageOptions"
        :selectedItems="model"
        :sort-by="tableSort"
        :headers="store.getHeaders()"
        :must-sort="true"
        :hide-default-footer="hidePagination"
        :select-strategy="selectStrategy"
        @update:options="loadItems"
      >
        <template v-slot:item.actions="{ item }">
          <v-menu>
            <template v-slot:activator="{ props }">
              <v-btn
                class="dot-menu"
                icon="mdi-dots-vertical"
                size="small"
                density="comfortable"
                variant="text"
                v-bind="props"
              ></v-btn>
            </template>
            <v-list>
              <div v-if="editable">
                <v-list-item
                  data-test-id="dataTableEditButton"
                  density="compact"
                  :title="_editButtonTitle"
                  :prepend-icon="editButtonIcon"
                  @click="store.editItem(item as TableRecord)"
                />
                <v-list-item
                  data-test-id="dataTableRevertButton"
                  v-if="confirmMode === DialogConfirmMode.Revert"
                  density="compact"
                  :title="t('buttons.revert')"
                  prepend-icon="mdi-revert"
                  @click="store.confirmDelete(item)"
                />
                <v-list-item
                  data-test-id="dataTableDeleteButton"
                  v-if="confirmMode === DialogConfirmMode.Delete && showDelete"
                  density="compact"
                  :title="t('buttons.delete')"
                  prepend-icon="mdi-delete"
                  @click="store.confirmDelete(item)"
                />
              </div>
              <slot name="menuItems" :item="item"></slot>
            </v-list>
          </v-menu>
        </template>
        <!-- @vue-skip -->
        <template v-for="(_, name) in $slots" v-slot:[name]="slotData">
          <slot :name="name" v-bind="slotData" />
        </template>
      </v-data-table-server>
    </v-row>
    <DialogInfo
      v-model="store.showInfoDialog"
      :title="store.infoTitle"
      :message="store.infoMessage"
      @close="store.hideInfo()"
    />
    <DialogConfirm
      v-model="store.showDeleteDialog"
      :loading="store.workStatus?.deleteOne"
      :message="isProtected(store.selectedItem?.id) ? disableDeleteMessage : deleteMessage"
      :title="deleteTitle"
      :mode="isProtected(store.selectedItem?.id) ? DialogConfirmMode.Info : confirmMode"
      @cancel="store.finalizeDelete"
      @delete="onDelete"
      @revert="onDelete"
    >
      <template v-slot:confirmation-details>
        <slot name="confirmation-details"></slot>
      </template>
    </DialogConfirm>
    <slot name="footer"></slot>
  </v-container>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import { isDefined } from '@/composables/utils'
import { DialogConfirmMode } from '@/enums/DialogConfirmMode'
import { EditorMode } from '@/enums/EditorMode'
import { SearchType } from '@/enums/SearchType'
import { SelectStrategy } from '@/enums/SelectStrategy'
import { SqlOrder } from '@/enums/SqlOrder'
import { TableStoreId } from '@/enums/TableStoreId'
import type { ISort } from '@/interfaces/api/ISort'
import type { ITableStore } from '@/interfaces/api/ITableStore'
import type { ITableOptions } from '@/interfaces/ITableOptions'
import type { ITableSort } from '@/interfaces/ITableSort'
import { TableRecord } from '@/models/TableRecord'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useAppStore } from '@/stores/ui/AppStore'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import { computed, onBeforeMount, ref, watch, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import DialogConfirm from './dialogs/DialogConfirm.vue'

const { t } = useI18n({ useScope: 'global' })
const appStore = useAppStore()
const settingsStore = useSettingsStore()

// PRIVATE VARIABLES
let _previousSearch: string | undefined = ''

// PROPS & EMITS
const emit = defineEmits<{
  (e: 'delete'): void
  (e: 'new'): void
}>()

const props = defineProps({
  editable: { type: Boolean, default: true },
  showNew: { type: Boolean, default: true },
  disableDeleteMessage: { type: String },
  disableNew: { type: Boolean, default: false },
  deleteTitle: { type: String, default: '' },
  deleteMessage: { type: String, default: '' },
  editButtonTitle: { type: String, default: '' },
  showDelete: { type: Boolean, default: true },
  confirmMode: {
    type: String as PropType<DialogConfirmMode>,
    default: DialogConfirmMode.Delete
  },
  isLookup: { type: Boolean, default: false },
  minTableCount: { type: Number, default: 5 },
  noteTitle: { type: String, default: '' },
  protectedItemIds: {
    type: Array<number>,
    default: () => {
      return []
    }
  },
  searchType: { type: String as PropType<SearchType>, default: SearchType.FieldSearch },
  selectStrategy: {
    type: String as PropType<SelectStrategy>,
    default: SelectStrategy.Single
  },
  showRefresh: { type: Boolean, default: true },
  tableStoreId: {
    type: String as PropType<TableStoreId>,
    default: TableStoreId.People
  },
  title: { type: String, default: '' }
})

const store = TableStoreFactory.get(props.tableStoreId) as ITableStore

// LIFECYCLE HOOKS
onBeforeMount(async () => {
  // Get headers every time because they can change dynamically.
  store.updateHeaders()
  if (isDefined(props.editButtonTitle)) {
    _editButtonTitle.value = props.editButtonTitle
  }
})

// REACTIVE VARIABLES
const _editButtonTitle = ref<string>(t('buttons.edit'))
const model = defineModel<Array<TableRecord>>({ default: [] })

// COMPUTED PROPERTIES
const hidePagination = computed((): boolean => {
  if (appStore.isPrinting) {
    return true
  }
  return store.count <= props.minTableCount
})

// WATCHERS
watch(
  (): any => settingsStore.localeCode,

  () => {
    // Headers must change when a different language is selected.
    store.updateHeaders()
  }
)

watch(
  (): any => props.editButtonTitle,

  (value) => {
    if (isDefined(value)) {
      _editButtonTitle.value = value
    }
  }
)

// COMPUTED PROPERTIES
const editButtonIcon = computed(() => {
  return store.editorMode === EditorMode.View ? 'mdi-information' : 'mdi-pencil'
})

const tableSort = computed((): Array<ITableSort> => {
  return store.defaultSort.map((item: ISort): ITableSort => {
    const order = item.order === SqlOrder.ASC ? 'asc' : 'desc'
    return { key: item.field, order }
  })
})

// FUNCTIONS
const isProtected = (itemId?: number) => {
  if (!isDefined(itemId)) {
    return false
  }

  return props.protectedItemIds.some((value: number) => {
    return value === itemId
  })
}

const loadItems = async (tableOptions: ITableOptions) => {
  if (props.isLookup && !isDefined(tableOptions.search)) {
    store.cancelLoad(true)
    _previousSearch = ''
    return
  }
  store.searchType = props.searchType
  const defer = isDefined(tableOptions.search) && _previousSearch !== tableOptions.search

  await store.loadItems(defer, tableOptions)
  _previousSearch = tableOptions.search
}

const onDelete = async () => {
  try {
    await store.deleteOne(store.selectedItem!)
    emit('delete')
  } catch {
    // Do nothing
  }
}

const createNewItem = (): void => {
  emit('new')
}
</script>

<style lang="scss" scoped>
.button-new {
  margin-right: 0.5rem;
}

.data-table {
  margin-bottom: 1.5rem;
}

.dot-menu {
  padding: 0;
  margin: 0.1rem;
}

.main-container {
  margin-top: 0;
  padding-top: 0;
  margin-bottom: 0;
  padding-bottom: 0;
}

.table-row {
  margin-top: -1rem;
  padding-top: 0;
}

.table-title {
  margin: 0 0.5rem 0 0.25rem;
}

.top-row {
  height: 5.75rem;
}

.top-row-small {
  height: 2.5rem;
}

.with-search {
  margin-bottom: 0.5rem;
}
</style>
